import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useCustomFormik from "../../../../custom-hooks/useCustomFormik";
import {
    userResetPasswordFirstStepFields,
    userResetPasswordFirstStepSchemaFactory,
    userResetPasswordSecondStepFields,
    userResetPasswordSecondStepSchemaFactory,
    userResetPasswordThirdStepFields,
    userResetPasswordThirdStepSchemaFactory,
} from "../../../../validation-schemas/user-schemas/UserResetPasswordSchema";
import ResetPasswordFirstStep from "./ResetPasswordFirstStep";
import ResetPasswordThirdStep from "./ResetPasswordThirdStep";
import ResetPasswordSecondStep from "./ResetPasswordSecondStep";
import { UserServiceImpl } from "../../../../services/UserServiceImpl";
import { useAppDispatch } from "../../../../custom-hooks/useAppDispatch";
import { ResetPasswordStepsEnum } from "../../../../domain/enums/ResetPasswordStepsEnum";
import { useErrorHandler } from "../../../../custom-hooks/base-hooks/useErrorHandler";
import { StatusCodes } from "http-status-codes";
import ResetPasswordSuccess from "./ResetPasswordSuccess";

const ResetPassword: FC = () => {
    const [currentStep, setCurrentStep] = useState<ResetPasswordStepsEnum>(null);
    const [isResetPasswordCompleted, setIsResetPasswordCompleted] = useState(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const {
        isError: userNotFound,
        resetErrors: resetNotFoundErrors,
        errorKey: notFoundKey,
    } = useErrorHandler(StatusCodes.NOT_FOUND, "errors.response.custom.emailNotFound");
    const {
        isError: recoveryCodeIncorrect,
        resetErrors: resetIncorrectRecoveryCodeErrors,
        errorKey: recoveryCodeIncorrectKey,
    } = useErrorHandler(StatusCodes.FORBIDDEN, "errors.response.custom.recoveryCodeInvalid");
    const {
        isError: passwordCannotBeUsed,
        resetErrors: resetConflictErrors,
        errorKey: conflictKey,
    } = useErrorHandler(StatusCodes.CONFLICT, "errors.response.custom.passwordCannotBeUsed");
    const {
        form: firstStepForm,
        setErrorKey: setErrorKeyFirstStep,
        errorKey: errorKeyFirstStep,
    } = useCustomFormik({
        initial: null,
        fields: userResetPasswordFirstStepFields,
        schema: userResetPasswordFirstStepSchemaFactory(t),
        onSubmit: async (values) => {
            resetNotFoundErrors();
            const result = await dispatch(UserServiceImpl.sendRecoveryCodeThunk(values));
            if (result.meta.requestStatus !== "fulfilled") {
                setErrorKeyFirstStep("errors.common");
                return;
            }
            if (userNotFound()) {
                return;
            }
            setCurrentStep(ResetPasswordStepsEnum.STEP2);
        },
    });
    const {
        form: secondStepForm,
        setErrorKey: setErrorKeySecondStep,
        errorKey: errorKeySecondStep,
    } = useCustomFormik({
        initial: null,
        fields: userResetPasswordSecondStepFields,
        schema: userResetPasswordSecondStepSchemaFactory(t),
        onSubmit: async (values) => {
            resetIncorrectRecoveryCodeErrors();
            const user = await dispatch(UserServiceImpl.checkRecoveryCodeThunk({ ...values, ...firstStepForm.values }));
            if (user.meta.requestStatus !== "fulfilled") {
                setErrorKeySecondStep("errors.common");
                return;
            }
            if (recoveryCodeIncorrect()) {
                return;
            }
            setCurrentStep(ResetPasswordStepsEnum.STEP3);
        },
    });
    const {
        form: thirdStepForm,
        setErrorKey: setErrorKeyThirdStep,
        errorKey: errorKeyThirdStep,
    } = useCustomFormik({
        initial: null,
        fields: userResetPasswordThirdStepFields,
        schema: userResetPasswordThirdStepSchemaFactory(t),
        onSubmit: async (values) => {
            resetConflictErrors();
            if (values.password !== values.confirmPassword) {
                setErrorKeyThirdStep("errors.validation.passwordsDoNotMatch");
                return;
            }
            const user = await dispatch(
                UserServiceImpl.resetPassword({ ...values, ...firstStepForm.values, ...secondStepForm.values })
            );
            if (user.meta.requestStatus !== "fulfilled") {
                setErrorKeyThirdStep("errors.common");
                return;
            }
            if (passwordCannotBeUsed()) {
                return;
            }
            setCurrentStep(null);
            setIsResetPasswordCompleted(true);
        },
    });
    const stepVisibilityHandler = (step: ResetPasswordStepsEnum) => {
        return (value: boolean) => {
            if (currentStep === step) {
                return setCurrentStep(null);
            }
            if (value) {
                setCurrentStep(step);
            }
        };
    };

    return (
        <>
            <ResetPasswordFirstStep
                firstStepForm={firstStepForm}
                error={notFoundKey || errorKeyFirstStep}
                isVisible={currentStep === ResetPasswordStepsEnum.STEP1}
                setIsVisible={stepVisibilityHandler(ResetPasswordStepsEnum.STEP1)}
            />
            <ResetPasswordSecondStep
                firstStepForm={firstStepForm}
                secondStepForm={secondStepForm}
                error={recoveryCodeIncorrectKey || errorKeySecondStep}
                isVisible={currentStep === ResetPasswordStepsEnum.STEP2}
                setIsVisible={stepVisibilityHandler(ResetPasswordStepsEnum.STEP2)}
            />
            <ResetPasswordThirdStep
                firstStepForm={firstStepForm}
                secondStepForm={secondStepForm}
                thirdStepForm={thirdStepForm}
                error={conflictKey || errorKeyThirdStep}
                isVisible={currentStep === ResetPasswordStepsEnum.STEP3}
                setIsVisible={stepVisibilityHandler(ResetPasswordStepsEnum.STEP3)}
            />
            <ResetPasswordSuccess
                isResetPasswordCompleted={isResetPasswordCompleted}
                setIsResetPasswordCompleted={setIsResetPasswordCompleted}
            />
        </>
    );
};

export default ResetPassword;
